import { FC, useId } from 'react';

/**
 * The two pieces of artwork the access-setup screen draws, inlined as JSX
 * rather than shipped as `.svg` files: an `import x from './a.svg?url'` needs a
 * bundler asset loader, which a consumer of this source-distributed package
 * may not have. Paths and colours are copied from the Figma exports the apps
 * used to ship (`tool-loader-logo.svg`, `no-access-illustration.svg`).
 * Both are decorative — the screen's text carries the meaning.
 */

/**
 * `useId` output, made safe for an SVG `id` and a `url(#…)` reference. The
 * gradient ids have to be unique per instance: an inline `<svg>` puts them in
 * the document's id space, where two copies of a fixed id would make the
 * second logo paint with the first one's gradients.
 */
const useSvgIdPrefix = () =>
  `ps-access-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

const ToolLogo: FC = () => {
  const p = useSvgIdPrefix();
  const id = (n: number) => `${p}-paint${n}`;
  const url = (n: number) => `url(#${id(n)})`;

  return (
    <svg
      className="c-access-setup__card-logo"
      width="82"
      height="82"
      viewBox="0 0 82 82"
      preserveAspectRatio="none"
      overflow="visible"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M56.7302 28.4906C56.7302 32.6562 53.1111 36.0385 48.6665 36.0385H16.9716V20.9429H48.6665C53.1111 20.9429 56.7302 24.325 56.7302 28.4906Z"
        fill="#D1C5FC"
      />
      <path
        d="M21.7023 71.8371C19.0896 71.8371 16.9716 69.719 16.9716 67.1062V44.5117H41.1855V71.8371H21.7023Z"
        fill={url(0)}
      />
      <path
        d="M49.0755 8.30811C53.7818 8.30837 58.1021 9.8708 61.5577 12.4997C62.4033 13.1382 63.2036 13.8481 63.9323 14.6118C67.5529 18.3791 69.7154 23.5173 69.5378 29.151C69.1924 40.1277 59.7631 48.7121 48.635 48.7126H16.9741V36.0477H53.1819C55.1417 36.0477 56.7325 34.459 56.7327 32.4992V24.5214C56.7327 22.5613 55.1419 20.9705 53.1819 20.9705H16.9741V8.25556L49.0755 8.30811Z"
        fill={url(1)}
      />
      <path
        opacity="0.5"
        d="M53.1819 21.4121C54.899 21.4121 56.292 22.8032 56.2925 24.5202V32.4979C56.2925 34.2153 54.8993 35.6085 53.1819 35.6085H17.412V21.4121H53.1819Z"
        fill="#D8D1F7"
        fillOpacity="0.5"
        stroke={url(2)}
        strokeWidth="0.343161"
      />
      <path
        d="M49.0383 31.7317C50.8395 31.7317 52.3119 30.2785 52.3119 28.5012C52.3119 26.7241 50.8395 25.2709 49.0383 25.2709C47.2371 25.2709 45.7647 26.7241 45.7647 28.5012C45.7647 30.2785 47.2371 31.7317 49.0383 31.7317Z"
        fill={url(3)}
      />
      <path
        d="M53.1844 21.4026H45.0045C43.2872 21.4026 41.8951 22.7948 41.8951 24.5121V32.4898C41.8951 34.2071 43.2872 35.5993 45.0045 35.5993H53.1844C54.9017 35.5993 56.2939 34.2071 56.2939 32.4898V24.5121C56.2939 22.7948 54.9017 21.4026 53.1844 21.4026Z"
        fill={url(4)}
        stroke={url(5)}
        strokeWidth="0.343161"
      />
      <path
        d="M49.0409 31.7401C50.842 31.7401 52.3145 30.287 52.3145 28.5096C52.3145 26.7325 50.842 25.2793 49.0409 25.2793C47.2397 25.2793 45.7673 26.7325 45.7673 28.5096C45.7673 30.287 47.2397 31.7401 49.0409 31.7401Z"
        fill={url(6)}
      />
      <defs>
        <linearGradient
          id={id(0)}
          x1="41.1855"
          y1="71.8371"
          x2="14.8027"
          y2="35.6984"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#6A5CB9" />
          <stop offset="1" stopColor="#302953" />
        </linearGradient>
        <linearGradient
          id={id(1)}
          x1="16.9741"
          y1="8.25556"
          x2="45.202"
          y2="55.034"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#9886F9" />
          <stop offset="1" stopColor="#6A5CB9" />
        </linearGradient>
        <linearGradient
          id={id(2)}
          x1="30.3039"
          y1="25.7124"
          x2="34.3452"
          y2="37.6475"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" />
          <stop offset="1" stopColor="#BCB2F2" />
        </linearGradient>
        <linearGradient
          id={id(3)}
          x1="51.2731"
          y1="31.2569"
          x2="46.933"
          y2="21.1158"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#6A5CB9" />
          <stop offset="1" stopColor="#302953" />
        </linearGradient>
        <linearGradient
          id={id(4)}
          x1="43.6768"
          y1="17.4072"
          x2="49.3061"
          y2="33.3425"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" />
          <stop offset="1" stopColor="#CEC7F5" />
        </linearGradient>
        <linearGradient
          id={id(5)}
          x1="46.5786"
          y1="25.7037"
          x2="53.1775"
          y2="33.1926"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" />
          <stop offset="1" stopColor="#BCB2F2" />
        </linearGradient>
        <linearGradient
          id={id(6)}
          x1="51.2754"
          y1="31.2653"
          x2="46.9355"
          y2="21.1243"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#6A5CB9" />
          <stop offset="1" stopColor="#302953" />
        </linearGradient>
      </defs>
    </svg>
  );
};

// Figma Track/Trace geometry (node 25501:28960 "Ring"): a 140x140 rounded
// square, 2.5 inset, used for both the static track and the animated trace.
const RING_PATH =
  'M70 137.5H35.5C26.7479 137.5 18.3542 134.023 12.1655 127.835C5.97678 121.646 2.5 113.252 2.5 104.5V35.5C2.5 26.7479 5.97678 18.3542 12.1655 12.1655C18.3542 5.97678 26.7479 2.5 35.5 2.5H104.5C113.252 2.5 121.646 5.97678 127.835 12.1655C134.023 18.3542 137.5 26.7479 137.5 35.5V104.5C137.5 113.252 134.023 121.646 127.835 127.835C121.646 134.023 113.252 137.5 104.5 137.5H70Z';

/**
 * The tool-loader card (Figma "Access/Tool loader card"): a glowing, breathing
 * card with the tool logo and a tracing ring. `failed` stops every animation
 * and dims the logo.
 */
export const ToolLoaderCard: FC<{ failed?: boolean }> = ({ failed }) => (
  <div
    className={`c-access-setup__card-stage${
      failed ? ' c-access-setup__card-stage--failed' : ''
    }`}
    aria-hidden="true"
  >
    <div className="c-access-setup__glow" />
    <div className="c-access-setup__ripple" />
    <div className="c-access-setup__ripple c-access-setup__ripple--late" />
    <div className="c-access-setup__card-wrap">
      <div className="c-access-setup__card">
        <ToolLogo />
      </div>
      <svg
        className="c-access-setup__ring"
        width="140"
        height="140"
        viewBox="0 0 140 140"
        fill="none"
      >
        <path className="c-access-setup__ring-track" d={RING_PATH} />
        <path
          className="c-access-setup__ring-trace"
          d={RING_PATH}
          pathLength={1}
        />
      </svg>
    </div>
  </div>
);

/** The "folder with a lock" drawing of the no-access state (179x143). */
export const NoAccessIllustration: FC = () => (
  <svg
    className="c-access-setup__illustration"
    width="179"
    height="143"
    viewBox="0 0 179 143"
    preserveAspectRatio="none"
    overflow="visible"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M15.14 28.6C15.14 22.2818 20.2618 17.16 26.58 17.16H60.9L75.2 34.32H152.42C158.738 34.32 163.86 39.4418 163.86 45.76V120.12C163.86 126.438 158.738 131.56 152.42 131.56H26.58C20.2618 131.56 15.14 126.438 15.14 120.12V28.6Z"
      fill="#C5C0F0"
    />
    <path
      d="M15.14 51.48C15.14 45.1618 20.2618 40.04 26.58 40.04H152.42C158.738 40.04 163.86 45.1618 163.86 51.48V120.12C163.86 126.438 158.738 131.56 152.42 131.56H26.58C20.2618 131.56 15.14 126.438 15.14 120.12V51.48Z"
      fill="#D6D2F4"
    />
    <path
      d="M102.37 74.36H76.63C73.4709 74.36 70.91 76.9209 70.91 80.08V100.1C70.91 103.259 73.4709 105.82 76.63 105.82H102.37C105.529 105.82 108.09 103.259 108.09 100.1V80.08C108.09 76.9209 105.529 74.36 102.37 74.36Z"
      fill="#A8A3D8"
    />
    <path
      d="M78.06 74.36V65.78C78.06 59.4618 83.1818 54.34 89.5 54.34C95.8182 54.34 100.94 59.4618 100.94 65.78V74.36"
      stroke="#A8A3D8"
      strokeWidth="5.72"
      strokeLinecap="round"
    />
    <path
      d="M89.5 91.52C91.8693 91.52 93.79 89.5993 93.79 87.23C93.79 84.8607 91.8693 82.94 89.5 82.94C87.1307 82.94 85.21 84.8607 85.21 87.23C85.21 89.5993 87.1307 91.52 89.5 91.52Z"
      fill="#D6D2F4"
    />
    <path
      d="M90.93 91.52C90.93 90.7302 90.2898 90.09 89.5 90.09C88.7102 90.09 88.07 90.7302 88.07 91.52V95.81C88.07 96.5998 88.7102 97.24 89.5 97.24C90.2898 97.24 90.93 96.5998 90.93 95.81V91.52Z"
      fill="#D6D2F4"
    />
  </svg>
);
